/**
 * True when an RQB conditions tree carries no predicate at all.
 *
 * Sibling to `validateConditionsTree`: both take a raw `conditions_json` value
 * and answer a question about it that every rule dialog using the QueryBuilder
 * needs, so both live here rather than in one feature's utils.
 *
 * The builder initialises an untouched tree to `{combinator: 'and', rules: []}`;
 * that has to reach the BE as `null` so the rule keeps whatever conditions
 * representation it had instead of compiling to an empty CEL group.
 *
 * Only the *root* node is examined, and only when it is a group (`combinator` +
 * `rules`) can it be empty — and then only when its `rules` is an actual empty
 * array. Reading no deeper is what keeps a leaf that quantifies over a
 * collection field from being misread: its operand slot holds a nested group of
 * predicates, so a walk that recursed would find a `combinator` + `rules` pair
 * one level down and could report a fully authored rule as carrying nothing.
 * Every other shape — a bare root leaf, or a malformed group whose `rules`
 * merely happens to be falsy — counts as carrying
 * a predicate, so it travels to the BE and gets a 400 there rather than being
 * silently normalised to `null` and dropped. Mirrors the backend's own
 * `_is_empty_conditions_tree` case for case, down to treating `{}` as empty (a
 * falsy dict on that side). The two must agree: whichever side is more lenient
 * discards conditions the other would have rejected out loud.
 */

import type { RuleGroupType } from 'react-querybuilder';

export const isEmptyConditionsTree = (tree: RuleGroupType | Record<string, unknown> | null | undefined): boolean => {
  if (!tree || Object.keys(tree).length === 0) {
    return true;
  }
  if ('combinator' in tree && 'rules' in tree) {
    return Array.isArray(tree.rules) && tree.rules.length === 0;
  }
  return false;
};
